import React from "react";
import { useTranslation } from "react-i18next";
import {
  futureSummary,
  selectedHistory,
  statisticObservation,
  conversionObservation,
} from "../helpers/teamAnalysis";
import { homeAdvantage } from "../helpers/teamSettingsImpact";
const fields = ["home_for", "home_against", "away_for", "away_against"];
export function referenceAudit(state) {
  if (!state) return [];
  const ids = state.active_team_ids || Object.keys(state.teams || {});
  return [...Object.keys(state.reference?.statistics || {}), "conversion"].map(
    (key) => ({
      key,
      values: fields.map((field) => {
        const values = ids.map((id) =>
          key === "conversion"
            ? state.teams[id]?.conversion?.[field]
            : state.teams[id]?.statistics?.[key]?.[field]
        );
        const valid = values.filter(Number.isFinite);
        const mean =
          ids.length && valid.length === ids.length
            ? valid.reduce((a, b) => a + b, 0) / ids.length
            : null;
        const stored =
          key === "conversion"
            ? state.reference?.conversion?.[field]
            : state.reference?.statistics?.[key]?.[field];
        return {
          mean,
          stored,
          count: valid.length,
          total: ids.length,
          matches:
            Number.isFinite(stored) &&
            mean !== null &&
            Math.abs(stored - mean) < 1e-10,
        };
      }),
    })
  );
}

export default function TeamModelReferences({
  data,
  teamIds = [],
  en = false,
}) {
  const { i18n } = useTranslation(),
    state = data.state,
    parameters = data.result_parameters;
  const t = (d, e) => (en ? e : d);
  const fmt = (v, d = 3) =>
    Number.isFinite(v)
      ? v.toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        })
      : "—";
  const labels = [
    t("Heim · Für", "Home · for"),
    t("Heim · Gegen", "Home · against"),
    t("Auswärts · Für", "Away · for"),
    t("Auswärts · Gegen", "Away · against"),
  ];
  const keys = [
    ...new Set([
      ...Object.keys(state?.reference?.statistics || {}),
      ...Object.values(state?.teams || {}).flatMap((x) =>
        Object.keys(x.statistics || {})
      ),
    ]),
    "conversion",
  ];
  const percent = (key) => key === "conversion" || Number(key) === 45;
  const display = (key, v) =>
    fmt(Number.isFinite(v) ? v * (percent(key) ? 100 : 1) : null) +
    (percent(key) ? " %" : "");
  const title = (key) =>
    key === "conversion"
      ? "Conversion Rate"
      : i18n.t(
          "help.automation.business.statisticNames." +
            state?.registry?.[key]?.title,
          { defaultValue: state?.registry?.[key]?.title || key }
        );
  const final = (data.observations || []).filter(
    (g) =>
      g.final &&
      Number.isFinite(g.goals?.home) &&
      Number.isFinite(g.goals?.away)
  );
  const drawRow = (model) => {
    const games = final.filter((g) => {
      const p = g[model]?.probabilities;
      return (
        p &&
        [p.homewinprob, p.drawprob, p.awaywinprob].every(
          (v) => Number.isFinite(v) && v >= 0 && v <= 1
        ) &&
        Math.abs(p.homewinprob + p.drawprob + p.awaywinprob - 1) < 0.0001
      );
    });
    const report =
      data.league_draws?.[model === "result" ? "result" : "statistics"];
    const count =
      report?.count ??
      (final.length === data.league_actual?.games ? games.length : 0);
    const total = report?.selected ?? data.league_actual?.games ?? final.length;
    const actual =
      report?.actual ??
      games.filter((g) => g.goals.home === g.goals.away).length;
    const expected =
      report?.expected ??
      games.reduce((n, g) => n + g[model].probabilities.drawprob, 0);
    return (
      <tr key={model}>
        <th>
          {model === "result"
            ? t("Ergebnismodell", "Results model")
            : t("Statistikmodell", "Statistics model")}
        </th>
        <td>
          {count}/{total}
        </td>
        <td>
          {count ? fmt(actual, 0) : "—"} / {count ? fmt(expected, 2) : "—"}
        </td>
        <td>
          {count ? fmt((100 * actual) / count, 2) : "—"} /{" "}
          {count ? fmt((100 * expected) / count, 2) : "—"} %
        </td>
        <td>
          {count && actual ? fmt(count / actual, 2) : "—"} /{" "}
          {count && expected ? fmt(count / expected, 2) : "—"}
        </td>
      </tr>
    );
  };
  return (
    <section
      className="ta-section ta-saved-parameters"
      aria-label="Season Parameters"
    >
      <h3>
        {t(
          "Season Parameters · aktuell gespeichert",
          "Season Parameters · currently saved"
        )}
      </h3>
      <p>
        {t(
          "Gespeicherte Liga-Referenzen. Entwürfe werden hier nicht als gespeichert angezeigt.",
          "Saved league references. Drafts are not shown as saved here."
        )}
      </p>
      <h4>{t("Ergebnismodell", "Results model")}</h4>
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              {[
                t("Toreschnitt", "Goals/game"),
                t("Heimvorteil", "Home advantage"),
                t("Auswärtswert · 2 − Heim", "Away value · 2 − home"),
                t("Quotient Heim / Auswärts", "Home / away ratio"),
                t("Remisfaktor", "Draw factor"),
              ].map((x) => (
                <th key={x}>{x}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{fmt(parameters?.stored_goal_average)}</td>
              <td>{fmt(parameters?.exp_home_factor)}</td>
              <td>
                {fmt(
                  Number.isFinite(parameters?.exp_home_factor)
                    ? 2 - parameters.exp_home_factor
                    : null
                )}
              </td>
              <td>
                {fmt(
                  Number.isFinite(parameters?.exp_home_factor) &&
                    parameters.exp_home_factor < 2
                    ? parameters.exp_home_factor /
                        (2 - parameters.exp_home_factor)
                    : null
                )}
              </td>
              <td>{fmt(parameters?.drawfactor)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <details>
        <summary>
          {t(
            "Ligaweite Remisbilanz · tatsächlich / erwartet",
            "League draw record · actual / expected"
          )}
        </summary>
        <p>
          {t(
            "Je Modell dieselben abgeschlossenen Spiele; Erwartungen bleiben die gespeicherten Spielprognosen.",
            "Same completed fixtures per model; expectations remain the saved match forecasts."
          )}
        </p>
        <div className="ta-scroll">
          <table>
            <thead>
              <tr>
                {[
                  t("Modell", "Model"),
                  t("Spiele / Abdeckung", "Games / coverage"),
                  t("Anzahl", "Count"),
                  t("Anteil", "Percentage"),
                  t("Faire Quote", "Fair odds"),
                ].map((x) => (
                  <th key={x}>{x}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drawRow("result")}
              {drawRow("statistics_model")}
            </tbody>
          </table>
        </div>
      </details>
      <h4>
        {t(
          "Statistikmodell · gespeicherte Liga-Referenzen",
          "Statistics model · saved league references"
        )}
      </h4>
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("Parameter", "Parameter")}</th>
              {labels.map((l) => (
                <th key={l}>{l}</th>
              ))}
              <th>{t("Gewicht", "Weight")}</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key}>
                <th>{title(key)}</th>
                {fields.map((f) => (
                  <td key={f}>
                    {display(
                      key,
                      key === "conversion"
                        ? state?.reference?.conversion?.[f]
                        : state?.reference?.statistics?.[key]?.[f]
                    )}
                  </td>
                ))}
                <td>
                  {key === "conversion" ? "—" : fmt(state?.weights?.[key])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        {t(
          "Updatefaktoren · Statistik / Conversion",
          "Update factors · statistics / conversion"
        )}
        : {fmt(state?.parameters?.statistics_update_factor)} /{" "}
        {fmt(state?.parameters?.conversion_update_factor)} ·{" "}
        {t("Standard-Conversion", "Standard conversion")}:{" "}
        {display("conversion", data.standard_conversion_rate)}
      </p>
      {teamIds.map((id) => {
        const games = selectedHistory(data.observations || [], id),
          team = state?.teams?.[id];
        return (
          <section key={id}>
            <h3>
              {data.result_teams?.[id]?.team_name || t("Mannschaft", "Team")} ·{" "}
              {t(
                "Gespeicherte Teamwerte und Liga-Referenz",
                "Saved team values and league reference"
              )}
            </h3>
            <div className="ta-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t("Modell", "Model")}</th>
                    {labels.map((l) => (
                      <th key={l}>{l}</th>
                    ))}
                    <th>{t("Quotient", "Ratio")}</th>
                    <th>{t("Toreschnitt", "Goals/game")}</th>
                    <th>{t("Heimvorteilsquotient", "Home advantage ratio")}</th>
                  </tr>
                </thead>
                <tbody>
                  {["result", "statistics"].map((model) => {
                    const r = futureSummary(
                      model === "result"
                        ? data.result_teams?.[id]
                        : data.ranking?.find((x) => x.team_id === Number(id))
                    );
                    return (
                      <tr key={model}>
                        <th>
                          {model === "result"
                            ? t("Ergebnismodell", "Results model")
                            : t("Statistikmodell", "Statistics model")}
                        </th>
                        {[
                          "for_home",
                          "against_home",
                          "for_away",
                          "against_away",
                          "total_strength",
                          "total_goals",
                        ].map((f) => (
                          <td key={f}>{fmt(r?.[f])}</td>
                        ))}
                        <td>{fmt(homeAdvantage(r))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {keys.map((key) => (
              <details key={key} open={key === "conversion"}>
                <summary>{title(key)}</summary>
                <div className="ta-scroll">
                  <table>
                    <thead>
                      <tr>
                        {[
                          t("Ausprägung", "Component"),
                          t("Team · gespeichert", "Team · saved"),
                          "Season Parameter",
                          t("Bisher erwartet", "Expected so far"),
                          t("Erspielt", "Observed"),
                          t(
                            "Spiele erwartet / real",
                            "Games expected / actual"
                          ),
                          ...(key === "conversion"
                            ? [
                                t(
                                  "Tore / Attack Moments · erwartet",
                                  "Goals / Attack Moments · expected"
                                ),
                                t(
                                  "Tore / Attack Moments · real",
                                  "Goals / Attack Moments · actual"
                                ),
                              ]
                            : []),
                        ].map((l) => (
                          <th key={l}>{l}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((f, i) => {
                        const e =
                            key === "conversion"
                              ? conversionObservation(games, id, f, true)
                              : statisticObservation(games, id, key, f, true),
                          a =
                            key === "conversion"
                              ? conversionObservation(games, id, f)
                              : statisticObservation(games, id, key, f);
                        const norm = (v) =>
                          Number(key) === 45 && v > 1 ? v / 100 : v;
                        return (
                          <tr key={f}>
                            <th>{labels[i]}</th>
                            <td>
                              {display(
                                key,
                                key === "conversion"
                                  ? team?.conversion?.[f]
                                  : team?.statistics?.[key]?.[f]
                              )}
                            </td>
                            <td>
                              {display(
                                key,
                                key === "conversion"
                                  ? state?.reference?.conversion?.[f]
                                  : state?.reference?.statistics?.[key]?.[f]
                              )}
                            </td>
                            <td>{display(key, norm(e.mean))}</td>
                            <td>{display(key, norm(a.mean))}</td>
                            <td>
                              {e.count}/{e.selected} · {a.count}/{a.selected}
                            </td>
                            {key === "conversion" && (
                              <>
                                <td>
                                  {fmt(e.goals)} / {fmt(e.moments)}
                                </td>
                                <td>
                                  {fmt(a.goals, 0)} / {fmt(a.moments)}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </section>
        );
      })}
      <details>
        <summary>
          {t(
            "Konsistenz der gespeicherten Statistikreferenzen",
            "Saved statistics reference consistency"
          )}
        </summary>
        {referenceAudit(state)
          .filter((r) => r.values.some((v) => !v.matches))
          .map((r) => (
            <p key={r.key}>
              {title(r.key)}:{" "}
              {t(
                "Gespeicherte Referenz weicht von den aktuellen Teamdurchschnitten ab oder ist unvollständig.",
                "Saved reference differs from current team means or is incomplete."
              )}
            </p>
          ))}
        {state &&
          referenceAudit(state).every((r) =>
            r.values.every((v) => v.matches)
          ) && (
            <p>
              {t(
                "Alle Ausprägungen stimmen mit den aktuellen Teamdurchschnitten überein.",
                "All components match the current team means."
              )}
            </p>
          )}
      </details>
    </section>
  );
}
